import { formatDistanceToNow } from "date-fns";
import { ChargeOnSendSwitch } from "@/modules/billing/ChargeOnSendSwitch";
import { useGetOne, type Identifier } from "ra-core";
import { Link } from "react-router";
import {
  AtSign,
  Briefcase,
  Building2,
  CalendarClock,
  CircleDot,
  Factory,
  FileText,
  Globe,
  Linkedin,
  MapPin,
  Megaphone,
  Phone,
  Sparkles,
  Tag,
  UserRound,
  CreditCard,
  Zap,
} from "lucide-react";
import type {
  Company,
  Contact,
  OrganizationMember,
} from "@/components/atomic-crm/types";
import { getClientShowPath } from "@/app/routing";
import { LBS_COMPANY_INDUSTRY_CHOICES } from "@/modules/leads/leadFormConstants";
import { CONTACT_STATUS_CHOICES } from "@/modules/constants/contactStatus";
import { CLIENT_PAYMENT_METHODS } from "../../../../supabase/functions/_shared/processingFees.ts";
import {
  buildCompanyEmailsPatch,
  buildCompanyPhonesPatch,
  getCompanyEmailRows,
  getCompanyPhoneRows,
} from "@/modules/clients/companyChannelPatch";
import { COMPANY_CHANNEL_TYPE_CHOICES } from "@/modules/clients/clientChannels";
import { formatUsPhoneDisplayFromAny } from "@/utils/phone";
import {
  FieldGroup,
  FieldRow,
  InlineChannelList,
  InlineChannels,
  InlineSearchPicker,
  InlineSelect,
  InlineText,
  ReadOnlyValue,
  useInlineSave,
} from "@/modules/shared/record/RecordFieldKit";

const memberLabel = (member: Partial<OrganizationMember>) =>
  [member.first_name, member.last_name].filter(Boolean).join(" ").trim() ||
  member.email ||
  "Team member";

const MemberName = ({ id }: { id?: Identifier | null }) => {
  const { data } = useGetOne<OrganizationMember>(
    "organization_members",
    { id: id ?? "" },
    { enabled: id != null, staleTime: 300_000 },
  );
  return <span className="truncate">{data ? memberLabel(data) : "…"}</span>;
};

const OwnerField = ({
  resource,
  record,
}: {
  resource: "companies" | "contacts";
  record: Company | Contact;
}) => (
  <InlineSearchPicker
    resource={resource}
    record={record}
    source="organization_member_id"
    placeholder="Owner"
    searchResource="organization_members"
    searchInBrowser
    filter={{ "disabled@neq": true }}
    toOption={(row) => ({
      id: row.id,
      label: memberLabel(row as OrganizationMember),
      detail: (row as OrganizationMember).email ?? undefined,
    })}
    display={<MemberName id={record.organization_member_id} />}
  />
);

const ago = (value?: string | null) =>
  value ? formatDistanceToNow(new Date(value), { addSuffix: true }) : undefined;

const websiteHref = (value: string) =>
  value.startsWith("http") ? value : `https://${value}`;

const normalizeWebsite = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
};

/** Account fields grouped like a record page; every value edits in place. */
export const CompanyRecordFields = ({ record }: { record: Company }) => {
  const { save } = useInlineSave("companies", record);
  return (
    <div className="px-4">
      <FieldGroup title="General">
        <FieldRow icon={Globe} label="Domain">
          <InlineText
            resource="companies"
            record={record}
            source="website"
            placeholder="Domain"
            normalize={normalizeWebsite}
            display={(value) => (
              <a
                href={websiteHref(value)}
                target="_blank"
                rel="noreferrer"
                className="truncate rounded-full border px-2 py-0.5 text-xs hover:bg-accent"
                onClick={(event) => event.stopPropagation()}
              >
                {value.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
              </a>
            )}
          />
        </FieldRow>
        <FieldRow icon={UserRound} label="Account owner">
          <OwnerField resource="companies" record={record} />
        </FieldRow>
        <FieldRow icon={Factory} label="Industry">
          <InlineSelect
            resource="companies"
            record={record}
            source="sector"
            placeholder="Industry"
            choices={LBS_COMPANY_INDUSTRY_CHOICES}
          />
        </FieldRow>
        <FieldRow icon={AtSign} label="Emails">
          <InlineChannelList
            rows={getCompanyEmailRows(record)}
            valueKey="email"
            placeholder="Email"
            typeChoices={COMPANY_CHANNEL_TYPE_CHOICES}
            onSave={(rows) => save(buildCompanyEmailsPatch(record, rows))}
          />
        </FieldRow>
        <FieldRow icon={Phone} label="Phones">
          <InlineChannelList
            rows={getCompanyPhoneRows(record)}
            valueKey="number"
            placeholder="Phone"
            typeChoices={COMPANY_CHANNEL_TYPE_CHOICES}
            renderValue={formatUsPhoneDisplayFromAny}
            onSave={(rows) => save(buildCompanyPhonesPatch(record, rows))}
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup title="Billing">
        <FieldRow icon={CreditCard} label="Payment method">
          <InlineSelect
            resource="companies"
            record={record}
            source="payment_method"
            placeholder="Default"
            choices={CLIENT_PAYMENT_METHODS}
          />
        </FieldRow>
        <FieldRow icon={Zap} label="Charge on send">
          <ChargeOnSendSwitch resource="companies" id={record.id} />
        </FieldRow>
      </FieldGroup>

      <FieldGroup title="Address">
        <FieldRow icon={MapPin} label="Street">
          <InlineText
            resource="companies"
            record={record}
            source="address"
            placeholder="Street"
          />
        </FieldRow>
        <FieldRow icon={MapPin} label="City">
          <InlineText
            resource="companies"
            record={record}
            source="city"
            placeholder="City"
          />
        </FieldRow>
        <FieldRow icon={MapPin} label="State">
          <InlineText
            resource="companies"
            record={record}
            source="state_abbr"
            placeholder="State"
            normalize={(value) => value.trim().toUpperCase() || null}
          />
        </FieldRow>
        <FieldRow icon={MapPin} label="ZIP">
          <InlineText
            resource="companies"
            record={record}
            source="zipcode"
            placeholder="ZIP"
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup title="Social">
        <FieldRow icon={Linkedin} label="LinkedIn">
          <InlineText
            resource="companies"
            record={record}
            source="linkedin_url"
            placeholder="LinkedIn"
            normalize={normalizeWebsite}
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup title="Notes">
        <FieldRow icon={FileText} label="Description">
          <InlineText
            resource="companies"
            record={record}
            source="description"
            placeholder="Description"
            multiline
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup title="System" defaultOpen={false}>
        <FieldRow icon={CalendarClock} label="Created">
          <ReadOnlyValue empty="—">{ago(record.created_at)}</ReadOnlyValue>
        </FieldRow>
        <FieldRow icon={CircleDot} label="Client">
          <ReadOnlyValue empty="—">
            {record.is_client ? "Yes" : "No"}
          </ReadOnlyValue>
        </FieldRow>
      </FieldGroup>
    </div>
  );
};

const CompanyName = ({ id }: { id: Identifier }) => {
  const { data } = useGetOne<Company>(
    "companies",
    { id },
    { staleTime: 60_000 },
  );
  return (
    <Link
      to={getClientShowPath(id)}
      className="truncate font-medium hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {data?.name ?? "…"}
    </Link>
  );
};

/** Person fields grouped like a record page; every value edits in place. */
export const ContactRecordFields = ({ record }: { record: Contact }) => (
  <div className="px-4">
    <FieldGroup title="General">
      <FieldRow icon={AtSign} label="Emails">
        <InlineChannels
          resource="contacts"
          record={record}
          source="email_jsonb"
          valueKey="email"
          placeholder="Email"
        />
      </FieldRow>
      <FieldRow icon={Phone} label="Phones">
        <InlineChannels
          resource="contacts"
          record={record}
          source="phone_jsonb"
          valueKey="number"
          placeholder="Phone"
          renderValue={formatUsPhoneDisplayFromAny}
        />
      </FieldRow>
      <FieldRow icon={Tag} label="Status">
        <InlineSelect
          resource="contacts"
          record={record}
          source="status"
          placeholder="Status"
          choices={CONTACT_STATUS_CHOICES.map((choice) => ({
            id: choice.value,
            name: choice.label,
          }))}
        />
      </FieldRow>
    </FieldGroup>

    {record.company_id == null ? (
      // Billed through an account otherwise; the account's method applies.
      <FieldGroup title="Billing">
        <FieldRow icon={CreditCard} label="Payment method">
          <InlineSelect
            resource="contacts"
            record={record}
            source="payment_method"
            placeholder="Default"
            choices={CLIENT_PAYMENT_METHODS}
          />
        </FieldRow>
        <FieldRow icon={Zap} label="Charge on send">
          <ChargeOnSendSwitch resource="contacts" id={record.id} />
        </FieldRow>
      </FieldGroup>
    ) : null}

    <FieldGroup title="Work">
      <FieldRow icon={Building2} label="Account">
        <InlineSearchPicker
          resource="contacts"
          record={record}
          source="company_id"
          placeholder="Account"
          searchResource="companies"
          toOption={(row) => ({
            id: row.id,
            label: (row as Company).name,
            detail: (row as Company).city ?? undefined,
          })}
          display={
            record.company_id != null ? (
              <CompanyName id={record.company_id} />
            ) : null
          }
        />
      </FieldRow>
      <FieldRow icon={Briefcase} label="Job title">
        <InlineText
          resource="contacts"
          record={record}
          source="title"
          placeholder="Job title"
        />
      </FieldRow>
      <FieldRow icon={UserRound} label="Owner">
        <OwnerField resource="contacts" record={record} />
      </FieldRow>
    </FieldGroup>

    <FieldGroup title="Sales">
      <FieldRow icon={Megaphone} label="Lead source">
        <InlineText
          resource="contacts"
          record={record}
          source="lead_source"
          placeholder="Lead source"
        />
      </FieldRow>
      <FieldRow icon={Sparkles} label="Services">
        <InlineText
          resource="contacts"
          record={record}
          source="interested_service"
          placeholder="Interested services"
        />
      </FieldRow>
    </FieldGroup>

    <FieldGroup title="Address">
      <FieldRow icon={MapPin} label="Street">
        <InlineText
          resource="contacts"
          record={record}
          source="address"
          placeholder="Street"
        />
      </FieldRow>
      <FieldRow icon={MapPin} label="City">
        <InlineText
          resource="contacts"
          record={record}
          source="city"
          placeholder="City"
        />
      </FieldRow>
      <FieldRow icon={MapPin} label="State">
        <InlineText
          resource="contacts"
          record={record}
          source="state_abbr"
          placeholder="State"
          normalize={(value) => value.trim().toUpperCase() || null}
        />
      </FieldRow>
      <FieldRow icon={MapPin} label="ZIP">
        <InlineText
          resource="contacts"
          record={record}
          source="zipcode"
          placeholder="ZIP"
        />
      </FieldRow>
    </FieldGroup>

    <FieldGroup title="Social">
      <FieldRow icon={Linkedin} label="LinkedIn">
        <InlineText
          resource="contacts"
          record={record}
          source="linkedin_url"
          placeholder="LinkedIn"
          normalize={normalizeWebsite}
        />
      </FieldRow>
    </FieldGroup>

    <FieldGroup title="System" defaultOpen={false}>
      <FieldRow icon={CalendarClock} label="Added">
        <ReadOnlyValue empty="—">{ago(record.first_seen)}</ReadOnlyValue>
      </FieldRow>
      <FieldRow icon={CalendarClock} label="Last seen">
        <ReadOnlyValue empty="—">{ago(record.last_seen)}</ReadOnlyValue>
      </FieldRow>
    </FieldGroup>
  </div>
);
